import { z } from 'zod';
import {
  AnomalySeverity,
  AnomalyType,
  BusinessStatus,
  ConflictType,
  RuntimeStatus,
  TaskType,
} from './enums.js';
import { IsoDate, IsoDateTime, MoneyAmount, UuidString } from './primitives.js';

/**
 * Одна аномалия в RunOutput — structural-only (B-A.7 / R-BE.1).
 *
 * Backend НЕ формирует freeform `description` / `detail`. Frontend композирует текст:
 *   `m.anomaly_type_label({type}) + m.anomaly_body_{type}(params)`.
 *
 * `params` — сырые значения (metric/observed/expected/delta/threshold/uom_code/...)
 * для локализации на FE без UUID/Decimal leak в UI.
 */
export const Anomaly = z
  .object({
    id: UuidString,
    // F-sync — entity_id для колонки «Компания» в all-scope (BE list-handler денормит).
    entity_id: UuidString.optional(),
    anomaly_type: AnomalyType,
    /** UI-алиас для `anomaly_type`. */
    type: AnomalyType.optional(),
    severity: AnomalySeverity,
    subject_ref: z.string(),
    /** UI-алиас для `subject_ref`. */
    subject: z.string().optional(),
    /** Статус ревью аномалии. */
    status: z.enum(['open', 'reviewed', 'escalated', 'dismissed']).optional(),
    run_id: UuidString.optional(),
    detected_at: IsoDateTime.optional(),
    evidence_ids: z.array(UuidString).default([]),
    /** Structural параметры — metric/observed/expected/delta/threshold/uom_code для FE i18n. */
    params: z.record(z.unknown()).optional(),
  })
  .strict();
export type Anomaly = z.infer<typeof Anomaly>;

/**
 * SourceConflict в RunOutput (lean, не DB row).
 *
 * Structural-only: FE рендерит через `m.conflict_type_label({type}) + m.conflict_body(details)`.
 */
export const SourceConflictItem = z
  .object({
    id: UuidString,
    conflict_type: ConflictType,
    subject_ref: z.string(),
    sources: z.array(z.string()).min(2),
    /** Structural params для i18n на FE. Не freeform текст. */
    details: z.record(z.unknown()),
  })
  .strict();
export type SourceConflictItem = z.infer<typeof SourceConflictItem>;

/**
 * RecommendedAction — structural-only (R-BE.3).
 *
 * Backend НЕ формирует freeform `title` / `description`. FE композирует текст:
 *   `m.recommended_action_title({action_type, ...params}) + m.recommended_action_body({action_type, ...params})`.
 */
export const RecommendedAction = z
  .object({
    id: UuidString,
    action_type: z.enum(['review', 'approve', 'escalate', 'fix', 'push']),
    priority: z.enum(['low', 'medium', 'high']),
    /** Structural params (ресурс/причина/ожидаемый исход) — FE локализует через i18n. */
    params: z.record(z.unknown()).optional(),
  })
  .strict();
export type RecommendedAction = z.infer<typeof RecommendedAction>;

/**
 * DraftArtifact — structural-only (R-BE.4).
 *
 * `title` не возвращается — FE рендерит через `m.draft_artifact_kind_label({kind})`.
 */
export const DraftArtifact = z
  .object({
    id: UuidString,
    kind: z.enum(['stock_entry', 'reconciliation', 'landed_cost', 'esf', 'kpn', 'excel', 'report']),
    payload_ref: z.string(), // URL или object key
  })
  .strict();
export type DraftArtifact = z.infer<typeof DraftArtifact>;

export const EvidenceRefLite = z
  .object({
    id: UuidString,
    kind: z.string(),
    label: z.string(),
  })
  .strict();
export type EvidenceRefLite = z.infer<typeof EvidenceRefLite>;

export const InventoryResult = z
  .object({
    opening_value: MoneyAmount,
    movements_value: MoneyAmount,
    closing_value: MoneyAmount,
    variance_value: MoneyAmount,
    items_reviewed: z.number().int().nonnegative(),
  })
  .strict();
export type InventoryResult = z.infer<typeof InventoryResult>;

export const WriteoffResult = z
  .object({
    total_writeoff_value: MoneyAmount,
    by_reason: z.record(MoneyAmount),
    items_affected: z.number().int().nonnegative(),
  })
  .strict();
export type WriteoffResult = z.infer<typeof WriteoffResult>;

export const CostResult = z
  .object({
    material_cost: MoneyAmount,
    labor_cost: MoneyAmount,
    overhead_cost: MoneyAmount,
    total_cost: MoneyAmount,
    scope_type: z.enum(['work_order', 'period', 'item', 'batch']),
    scope_id: z.string(),
  })
  .strict();
export type CostResult = z.infer<typeof CostResult>;

export const Observability = z
  .object({
    run_duration_ms: z.number().int().nonnegative(),
    llm_tokens: z
      .object({
        input: z.number().int().nonnegative(),
        output: z.number().int().nonnegative(),
      })
      .strict(),
    tool_calls: z.number().int().nonnegative(),
    tools_failed: z.number().int().nonnegative(),
    trace_id: z.string().nullable(),
  })
  .strict();
export type Observability = z.infer<typeof Observability>;

/** Canonical RunOutput JSON backbone. */
export const RunOutput = z
  .object({
    schema_version: z.string(),
    task_type: TaskType,
    run_id: UuidString,
    runtime_status: RuntimeStatus,
    status: BusinessStatus,
    period: z
      .object({
        date_from: IsoDate,
        date_to: IsoDate,
      })
      .strict(),
    scope: z.record(z.unknown()),
    source_of_truth_used: z.array(z.string()),
    inventory_result: InventoryResult.optional(),
    writeoff_result: WriteoffResult.optional(),
    cost_result: CostResult.optional(),
    source_conflicts: z.array(SourceConflictItem),
    anomalies: z.array(Anomaly),
    risk_flags: z.array(z.string()),
    missing_fields: z.array(z.string()),
    recommended_actions: z.array(RecommendedAction),
    approval_required: z.boolean(),
    draft_artifacts: z.array(DraftArtifact),
    evidence: z.array(EvidenceRefLite),
    observability: Observability,
    emitted_at: IsoDateTime,
  })
  .strict();
export type RunOutput = z.infer<typeof RunOutput>;

export const RUN_OUTPUT_SCHEMA_VERSION = '1.0.0';
